import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@coordio/ui';
import { LoadingSkeleton } from '@coordio/ui/layout';
import { Trash2 as DeleteIcon, Download as DownloadIcon, Eye as VisibilityIcon } from 'lucide-react';
import { fileService } from '../../services/fileService';
import type { AgentSessionFileDto, FileCategory } from '../../types/file';
import { useNotificationStore } from '../../store/notificationStore';
import { useConfirmDialog } from '../common/ConfirmDialog';
import { FilePreview } from './FilePreview';

export interface FileListProps {
  sessionId: string;
  refreshKey?: number;
}

const categoryLabel = (c: FileCategory) => {
  switch (c) {
    case 0:
      return 'Input';
    case 1:
      return 'Output';
    case 2:
      return 'Generated';
    case 3:
      return 'Attachment';
    case 4:
      return 'Temporary';
    case 5:
      return 'Artifact';
    case 6:
      return 'Log';
    default:
      return 'Unknown';
  }
};

const formatSize = (bytes: number) => {
  if (!bytes || bytes <= 0) return '0 B';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

/**
 * File list component for a session.
 */
export const FileList = ({ sessionId, refreshKey }: FileListProps) => {
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [files, setFiles] = useState<AgentSessionFileDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<AgentSessionFileDto | null>(null);

  const sorted = useMemo(() => {
    return [...files].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [files]);

  const load = async () => {
    setLoading(true);
    try {
      const result = await fileService.getSessionFiles(sessionId);
      setFiles(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load files';
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, refreshKey]);

  const handlePreview = (f: AgentSessionFileDto) => {
    setSelectedFile(f);
    setPreviewOpen(true);
  };

  const handleDownload = async (f: AgentSessionFileDto) => {
    try {
      const resp = await fileService.downloadFileBlob(f.id);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to download file';
      addNotification(msg, 'error');
    }
  };

  const handleDelete = async (f: AgentSessionFileDto) => {
    const ok = await confirm({
      title: 'Delete file',
      message: `Delete file "${f.fileName}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await fileService.deleteFile(f.id);
      addNotification('File deleted', 'success');
      await load();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete file';
      addNotification(msg, 'error');
    }
  };

  return (
    <div className="mt-1">
      {loading ? (
        <div className="flex justify-center py-2">
          <LoadingSkeleton />
        </div>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-1">No files yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {sorted.slice(0, 10).map((f) => (
            <li key={f.id} className="flex items-center justify-between py-2 px-1">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-sm font-medium">{f.fileName}</span>
                  <Chip label={categoryLabel(f.category)} variant="outlined" color="default" />
                  <Chip label={`v${f.version}`} variant="outlined" color="default" />
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatSize(f.fileSizeBytes)} • {new Date(f.createdAt).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-0.5 ml-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={() => handlePreview(f)} className="h-7 w-7">
                        <VisibilityIcon size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Preview</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={() => handleDownload(f)} className="h-7 w-7">
                        <DownloadIcon size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Download</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(f)} className="h-7 w-7 text-red-500 hover:text-red-700">
                        <DeleteIcon size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </li>
          ))}
          {sorted.length > 10 && (
            <li className="px-1 py-1">
              <span className="text-xs text-muted-foreground">
                Showing 10 of {sorted.length} files.
              </span>
            </li>
          )}
        </ul>
      )}

      <FilePreview open={previewOpen} file={selectedFile} onClose={() => setPreviewOpen(false)} />
    </div>
  );
};
